import { unstable_ClassNameGenerator as ClassNameGenerator } from '@mui/material/className';

ClassNameGenerator.configure((componentName: string) => componentName.replace('Mui', 'V5')); // load as early as possible this code in a js file
import '../../utils/MuiClassNameSetup';
import React from 'react';
import { Provider } from 'react-redux';
import { applyMiddleware, combineReducers, createStore } from 'redux';
import thunk from 'redux-thunk';
import { mdModule } from '@rakutenfrance/breakpoint';
import { createLogger } from '@rakutenfrance/logger';
import advertCrewPartnerModule from '@rakutenfrance/advert-crew-partner-card';
import { getServerSidePropsLoader, createAxiosInstance, useApollo } from '@rakutenfrance/redux-apollo-helper';
import { Wrapper } from '../../fragmentHelper/Wrapper';
import { renderReact } from '../../fragmentHelper/renderReactCustom';
import { GetServerSidePropsHypernovaWatchContext, GetServerSideProps } from '../../types';
import { hypernovaWrapperClassName } from '../../utils/hypernovaWrapperClassName';
import { API } from './urls';
import '@rakutenfrance/advert-crew-partner-card/build/style.global.css';
import { configService as configurationService } from '@rakutenfrance/config-service';
import { ApolloProvider } from '@apollo/client';
import { MUIProvider } from '@rakutenfrance/mui';
import { HttpsProxyAgent } from 'https-proxy-agent';
export { advertCrewPartnerModule };

const { AdvertCrewContainer }: any = advertCrewPartnerModule.containers;

const advertCrewPartnerReducer = advertCrewPartnerModule.reducer!;
const mdReducer = mdModule.reducer;

type PagePropsType = { preloadedState: any; initialApolloState: any; initialReduxState: any; moduleName: string };

declare global {
  interface Window {
    hypernovaAdvertCrewFPLazyInit: any;
  }
}

const WrappedAdvertCrewBuyBox: React.FC<PagePropsType> = ({ preloadedState, initialApolloState, initialReduxState, moduleName }) => {
  const createStoreWithMiddleware = applyMiddleware(thunk)(createStore);
  const reducer = combineReducers({
    advertCrewPartner: advertCrewPartnerReducer,
    md: mdReducer,
  });

  const store = createStoreWithMiddleware(reducer, preloadedState);
  const apolloClient = useApollo(initialApolloState);
  const advertCrewProps = {
    isBuyBox: true,
  };
  const htmlFontSize = 10; // SSR default font Size, used in Canopy

  // us a css based dynamic css fontsize ajust as in css files transpiled by the postcss pluging where all rem values get ajusted see 'postcss-rem-to-rem-multiplier'
  // this is needed because different monolith pages have different <html> fontsize values. e.g. 13px, 12px, 16px.
  // a js in monolith is setting the css var --hyp-rem-multi earl in the page load, this react js is loaded later
  // css will look like this calc(1.2rem * var(--hyp-rem-multi))

  const pxToRem = (size: number): string => `calc(${size / htmlFontSize}rem * var(--hyp-rem-multi))`;

  return (
    <Provider store={store}>
      <ApolloProvider client={apolloClient}>
        <MUIProvider moduleName={moduleName} htmlFontSize={htmlFontSize} pixToRem={pxToRem} prefixwrapClassName={hypernovaWrapperClassName}>
          <Wrapper>
            <AdvertCrewContainer {...advertCrewProps} />
          </Wrapper>
        </MUIProvider>
      </ApolloProvider>
    </Provider>
  );
};

export const renderFunction = renderReact('AdvertCrewBuyBoxPartner', WrappedAdvertCrewBuyBox);

if (typeof window !== 'undefined') {
  window.hypernovaAdvertCrewFPLazyInit = (): void => {
    renderReact('AdvertCrewBuyBoxPartner', WrappedAdvertCrewBuyBox);
  };
}

const modules = [advertCrewPartnerModule] as any;

export const serverCanCache = true;

export const getServerSideProps = async (watchContext: GetServerSidePropsHypernovaWatchContext): Promise<GetServerSideProps> => {
  const baseUrl = "";
  const preloadedConfig = {};
  const graphQlVariables = {};
  const logger = createLogger('Hypernova AdvertCrewBuyBoxPartner');
  const { initialReduxState, initialApolloState } = await getServerSidePropsLoader(
    watchContext,
    modules,
    baseUrl,
    preloadedConfig,
    undefined,
    graphQlVariables
  );

  const advertCrewModale = configurationService.get().advertCrewModale;

  const axiosInstance = createAxiosInstance({ context: watchContext, moduleName: 'axios advertCrewBuyBoxPartner' });
  let agent = undefined;
  if (process.env.HTTPS_PROXY_HOST && process.env.HTTPS_PROXY_PORT) {
    agent = new HttpsProxyAgent(`http://${process.env.HTTPS_PROXY_HOST}:${process.env.HTTPS_PROXY_PORT}`);
  }
  let data = null;
  if (watchContext.jobData.partnerKey) {
    const apiUrl = API.crewPartnerInfo + watchContext.jobData.partnerKey + '.json';

    data = await axiosInstance.get(apiUrl, {
      httpsAgent: agent,
    });
    logger.debug('buybox crew advert data', {
      partnerKey: watchContext.jobData.partnerKey,
      data: data && data.data ? JSON.stringify(data.data[watchContext.jobData.partnerKey]) : '',
    });
    return {
      props: {
        initialReduxState,
        initialApolloState,
        preloadedState: {
          advertCrewPartner: {
            advertReducer: watchContext.jobData,
            partnerReducer: data && data.data && data.data[watchContext.jobData.partnerKey],
            advertCrewModaleReducer: advertCrewModale,
          },
        },
      },
    };
  } else {
    const apiUrl = process.env.GATEWAY_URL + API.advertCrewInfo + watchContext.jobData.advertId;
    const data = await axiosInstance.get(apiUrl, { params: { productId: watchContext.jobData.productId } });
    logger.debug('buybox crew advert data', { data: data && data.data ? JSON.stringify(data.data) : '' });

    const crewPartnerInfoUrl = API.crewPartnerInfo + data?.data?.partnerKey + '.json';
    const partnerData = await axiosInstance.get(crewPartnerInfoUrl, {
      httpsAgent: agent,
    });

    return {
      props: {
        initialReduxState,
        initialApolloState,
        preloadedState: {
          advertCrewPartner: {
            advertReducer: data.data,
            partnerReducer: partnerData && partnerData.data && partnerData.data[data?.data?.partnerKey],
            advertCrewModaleReducer: advertCrewModale,
          },
        },
      },
    };
  }
};
